import { Fragment, type ReactNode } from "react";
import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export function PageHero({
  eyebrow,
  title,
  description,
  highlightWord,
  breadcrumbLabel,
  breadcrumbItems = [],
  children,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  highlightWord?: string;
  breadcrumbLabel?: string;
  breadcrumbItems?: { label: string; href: string }[];
  children?: ReactNode;
}) {
  const renderTitleWithHighlight = () => {
    if (!highlightWord) return title;
    
    const words = title.split(" ");
    const result: (string | JSX.Element)[] = [];
    
    words.forEach((word, index) => {
      if (word.toLowerCase() === highlightWord.toLowerCase()) {
        result.push(
          <span key={index} className="text-primary">
            {word}
          </span>
        );
      } else {
        result.push(word);
      }
      
      if (index < words.length - 1) {
        result.push(" ");
      }
    });
    
    return result;
  };

  return (
    <section className="relative mt-[90px] bg-foreground text-white overflow-hidden">
      <div className="absolute inset-0 bg-grid-overlay opacity-40 pointer-events-none" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1440px] px-4  py-20 sm:py-24 md:py-20">
        
        <span className="text-primary font-bold text-sm tracking-widest uppercase">{eyebrow}</span>
        <h1 className="heading-xl mt-4 text-[24px] md:text-4xl max-w-4xl break-words">
          {renderTitleWithHighlight()}
        </h1>
        {description && (
          <p className="text-card/60 mt-5 text-base max-w-4xl leading-relaxed">
            {description}
          </p>
        )}
        {children && 
        <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        <Breadcrumb className="mt-8">
          <BreadcrumbList className="text-white/60">
            <BreadcrumbItem>
              <BreadcrumbLink asChild className="text-white/70 hover:text-primary text-base">
                <Link href="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {breadcrumbItems.map((item) => (
              <Fragment key={item.href}>
                <BreadcrumbSeparator className="text-white/35 " />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild className="text-white/70 hover:text-primary text-base">
                    <Link href={item.href}>{item.label}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Fragment>
            ))}
            <BreadcrumbSeparator className="text-white/35 " />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-primary text-base">{breadcrumbLabel ?? eyebrow}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      
    </section>
  );
}
